import React, { useRef, useState } from "react";
import { Save, X, Check, CheckCircle2, Clock } from "lucide-react"; // Clock اضافه شد
import { useTranslation } from "react-i18next";
import { Editor } from "@tinymce/tinymce-react";
import CustomEditor from "../common/Editor";

export default function ExamMetaBar({
  meta,
  setMeta,
  level3Categories = [],
  onSave,
  onSaveAs,
  saving,
  onPublish,
  publishing,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const [showInlineSaveAs, setShowInlineSaveAs] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const scopeIds = Array.isArray(user?.categoryScopes)
    ? user.categoryScopes.map(String)
    : [];
  const scopeSet = new Set(scopeIds);

  // فیلتر دسته‌بندی‌ها (بدون تغییر)
  const visibleLevel3Categories =
    scopeSet.size === 0
      ? level3Categories
      : level3Categories.filter((c) => {
          const id = String(c._id);
          if (scopeSet.has(id)) return true;
          if (Array.isArray(c.ancestors) && c.ancestors.length) {
            return c.ancestors.some((aid) => scopeSet.has(String(aid)));
          }
          if (c.parent && scopeSet.has(String(c.parent))) return true;
          return false;
        });

  // هندلر تغییرات اصلی (Root Level)
  const onChange = (e) => {
    const { name, value } = e.target;
    setMeta((m) => ({ ...m, [name]: value }));
  };

  // هندلر تغییرات داخل آبجکت meta (Nested)
  const onMetaChange = (e) => {
    const { name, value } = e.target;
    setMeta((m) => ({ ...m, meta: { ...m.meta, [name]: value } }));
  };

  const handleDescriptionChange = (content) => {
    setMeta((m) => ({ ...m, description: content }));
  };

  const isImageSizeAuto = String(meta.meta?.imageSize) === "0";

  // Save As Logic (بدون تغییر)
  const openInlineSaveAs = () => {
    const suggested = meta.title ? `${meta.title} COPY` : "Exam Copy";
    setSaveAsName(suggested);
    setShowInlineSaveAs(true);
  };

  const confirmInlineSaveAs = async () => {
    const name = (saveAsName || "").trim();
    if (!name) return;
    await onSaveAs?.(name);
    setShowInlineSaveAs(false);
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 flex flex-col gap-4">
      {/* ردیف اول: اطلاعات اصلی */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
        {/* Title */}
        <div className="md:col-span-1">
          <label className="block text-sm font-medium mb-1">
            {t("exam_builder.meta.title")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            name="title"
            value={meta.title}
            onChange={onChange}
            placeholder={t("exam_builder.meta.title_ph")}
          />
        </div>

        {/* Category */}
        <div className="md:col-span-1">
          <label className="block text-sm font-medium mb-1">
            {t("exam_builder.meta.category")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            name="categoryId"
            value={meta.categoryId}
            onChange={onChange}
          >
            <option value="">{t("exam_builder.meta.category_ph")}</option>
            {visibleLevel3Categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.title} {c.level ? ` (L${c.level})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* --- بخش جدید مدیریت زمان (Time Mode) --- */}
        <div className="md:col-span-2 grid grid-cols-2 gap-3 border rounded-lg p-2 bg-gray-50">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              حالت زمان‌بندی
            </label>
            <select
              className="w-full border rounded text-sm px-2 py-1.5"
              name="timeMode"
              value={meta.timeMode || "per_section"}
              onChange={onChange}
            >
              <option value="per_section">
                زمان هر سکشن جدا (Per Section)
              </option>
              <option value="global">زمان کل آزمون (Total Time)</option>
            </select>
          </div>

          {/* نمایش اینپوت فقط اگر حالت Global باشد */}
          {meta.timeMode === "global" ? (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                زمان کل (دقیقه)
              </label>
              <input
                type="number"
                min="1"
                className="w-full border rounded text-sm px-2 py-1.5"
                name="totalTime" // در دیتابیس باید به عنوان زمان کل ذخیره شود
                value={meta.totalTime || ""}
                onChange={onChange}
                placeholder="مثلاً 60"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center text-xs text-gray-400 mt-4">
              زمان در تنظیمات هر سکشن تعیین می‌شود.
            </div>
          )}
        </div>
      </div>

      {/* ردیف دوم: تنظیمات ظاهری و توضیحات */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Header Color */}
        <div className="md:col-span-1">
          <label className="block text-sm font-medium mb-1">
            {t("exam_builder.meta.header_color")}
          </label>
          <div className="flex items-center gap-2 border rounded-lg px-2 py-1">
            <input
              type="color"
              name="headerColor"
              value={meta.meta.headerColor || "#7e22ce"}
              onChange={onMetaChange}
              className="h-8 w-8 rounded-md overflow-hidden"
            />
            <input
              type="text"
              name="headerColor"
              value={meta.meta.headerColor || "#7e22ce"}
              onChange={onMetaChange}
              className="flex-1 outline-none text-sm"
              placeholder="#rrggbb"
            />
          </div>
        </div>

        {/* Language Mode */}
        <div className="md:col-span-1">
          <label className="block text-sm font-medium mb-1">
            {t("exam_builder.meta.languageMode")}
          </label>
          <select
            value={meta.meta.languageMode || "auto"}
            className="w-full border p-2 rounded-md"
            onChange={(e) =>
              setMeta((m) => ({
                ...m,
                meta: { ...m.meta, languageMode: e.target.value },
              }))
            }
          >
            <option value="auto">Auto</option>
            <option value="en_only">English only</option>
            <option value="fa_only">Persian only</option>
            <option value="bilingual">Bilingual</option>
          </select>
        </div>

        {/* Image Size */}
        <div className="md:col-span-2">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                {t("exam_builder.meta.image_size")}
              </label>
              <input
                type="number"
                name="imageSize"
                value={isImageSizeAuto ? "" : meta.meta?.imageSize}
                onChange={(e) =>
                  setMeta((m) => ({
                    ...m,
                    meta: { ...m.meta, imageSize: e.target.value },
                  }))
                }
                placeholder={t("exam_builder.meta.image_size_ph")}
                disabled={isImageSizeAuto}
                className={`w-full border rounded-lg px-3 py-2 ${isImageSizeAuto ? "bg-gray-200" : ""}`}
              />
            </div>
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="imageSizeAuto"
                checked={isImageSizeAuto}
                onChange={(e) =>
                  setMeta((m) => ({
                    ...m,
                    meta: { ...m.meta, imageSize: e.target.checked ? "0" : "" },
                  }))
                }
                className="rounded text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="imageSizeAuto" className="text-sm text-gray-700">
                {t("exam_builder.meta.image_size_auto")}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div dir={isRtl ? "rtl" : "ltr"}>
        <label className="block text-sm font-medium mb-1">
          {t("exam_builder.meta.description")}
        </label>
        <CustomEditor
          value={meta.description}
          onEditorChange={handleDescriptionChange}
        />
      </div>

      {/* Actions (Save, Publish) */}
      <div
        className={`flex ${isRtl ? "justify-start" : "justify-end"} gap-2 flex-wrap border-t pt-4`}
      >
        {/* Save */}
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving
            ? t("exam_builder.saving_draft")
            : t("exam_builder.save_draft")}
        </button>

        {/* Save As Toggle */}
        {!showInlineSaveAs ? (
          <button
            type="button"
            onClick={openInlineSaveAs}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {t("exam_builder.save_as_draft")}
          </button>
        ) : (
          <div className="flex items-center gap-2 border rounded-xl px-2 py-1 bg-gray-50">
            <input
              type="text"
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              className="border rounded-lg px-2 py-1"
              placeholder="New Title..."
            />
            <button
              type="button"
              onClick={confirmInlineSaveAs}
              className="p-1.5 bg-green-600 text-white rounded-lg"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setShowInlineSaveAs(false)}
              className="p-1.5 bg-gray-400 text-white rounded-lg"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Publish */}
        <button
          type="button"
          onClick={onPublish}
          disabled={saving || publishing}
          className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-2"
        >
          <CheckCircle2 className="w-4 h-4" />
          {publishing
            ? t("exam_builder.publishing")
            : meta?.isPublished
              ? t("exam_builder.published")
              : t("exam_builder.publish")}
        </button>
      </div>
    </div>
  );
}
